import { useInvalidateUseGetAutopilotTasksForCampaign } from '@urbanx/agx.activities.client'
import { useStaffUpdateLaunchDateForCampaign } from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircleOutline,
} from '@urbanx/agx.ui-components2'
import { JSX, useState } from 'react'
import { AwaitingAgentApprovalBlocker } from './AwaitingAgentApprovalBlocker'

interface LaunchApprovalBlockerProps {
  campaignId: string
  contractId?: string | null
  onTasksInvalidated?: () => void | Promise<void>
}

export const LaunchApprovalBlocker = ({
  campaignId,
  contractId,
  onTasksInvalidated,
}: LaunchApprovalBlockerProps): JSX.Element => {
  const [isLaunchDateModalOpen, setIsLaunchDateModalOpen] = useState(false)
  const [isConfirmingApproval, setIsConfirmingApproval] = useState(false)
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()

  const refreshTasks = () => {
    if (onTasksInvalidated) return onTasksInvalidated()

    return invalidateTasks(campaignId, contractId)
  }

  const refreshTasksAfterApproval = async () => {
    await refreshTasks()
    await new Promise((resolve) => setTimeout(resolve, 1000))
    await refreshTasks()
  }

  const { staffUpdateLaunchDateForCampaign } =
    useStaffUpdateLaunchDateForCampaign({
      onSuccess: async () => {
        try {
          await refreshTasksAfterApproval()
          setIsLaunchDateModalOpen(false)
        } finally {
          setIsConfirmingApproval(false)
        }
      },
      onError: () => setIsConfirmingApproval(false),
    })

  const onConfirmLaunchDate = () => {
    if (!staffUpdateLaunchDateForCampaign) return

    setIsConfirmingApproval(true)
    staffUpdateLaunchDateForCampaign({
      campaignId,
      hasAgentApproval: true,
    })
  }

  return (
    <>
      <AwaitingAgentApprovalBlocker
        onApprovalReceived={() => setIsLaunchDateModalOpen(true)}
      />
      <AgxModal
        titleIcon={
          <div className="modelTitleIconContainer">
            <Icon_AlertCircleOutline type={'Danger'} height={40} width={40} />
          </div>
        }
        type="single"
        variant={'withoutDivider'}
        actionButtonContainerStyle={'withDivider'}
        actionButtons={
          <AgxSpacer
            direction="horizontal"
            gap={'spacing-md'}
            contentJustification="center"
            fill
          >
            <AgxButton
              id={'btnCancel'}
              text={'Cancel'}
              variant={'hollow'}
              onClick={() => setIsLaunchDateModalOpen(false)}
              stretch
            />
            <AgxButton
              id={'btnConfirm'}
              text={'Confirm'}
              variant={'primary'}
              onClick={onConfirmLaunchDate}
              disabled={isConfirmingApproval}
              stretch
            />
          </AgxSpacer>
        }
        content={
          <AgxSpacer
            direction="vertical"
            fill
            gap="sm"
            padding={{ bottom: 'xl', left: 'xl' }}
          >
            <AgxTypography
              id={'launchDateModalTitle'}
              type={'body_ui_strong'}
              size={'medium'}
              color="var(--text-primary)"
            >
              Agent approval received
            </AgxTypography>
            <AgxTypography
              id={'launchDateModalBody'}
              type={'body_ui'}
              size={'small'}
              color="var(--text-secondary)"
            >
                Launch tasks will unlock once confirmed.
            </AgxTypography>
          </AgxSpacer>
        }
        isOpen={isLaunchDateModalOpen}
        setIsDialogOpen={setIsLaunchDateModalOpen}
      />
    </>
  )
}
